/** Normalize opaque id-like cell values. */
function cleanId(raw) {
  if (raw == null) return null
  const s = String(raw).trim().replace(/\.0$/, '')
  if (!s) return null
  const lower = s.toLowerCase()
  if (lower === 'nan' || lower === 'none' || lower === 'null') return null
  return s
}

function cleanDisplayValue(raw) {
  if (raw == null) return null
  const s = String(raw).trim()
  if (!s) return null
  const lower = s.toLowerCase()
  if (lower === 'nan' || lower === 'none' || lower === 'null') return null
  return s
}

/** True when the row is tied to artist_mappings (PK or non-blank Artist Status). */
export function rowHasArtistMapping(row) {
  if (!row || typeof row !== 'object') return false
  const mappingId = cleanId(row.id)
  if (mappingId && /^\d+$/.test(mappingId)) return true
  return Boolean(cleanDisplayValue(row['Artist Status']))
}

/** Build Analyze deep-link query text from a dashboard row (fallback only). */
export function analyzeQueryFromRow(row) {
  if (!row || typeof row !== 'object') return null
  const pick = (...keys) => {
    for (const key of keys) {
      const raw = row[key]
      if (raw == null) continue
      const s = String(raw).trim()
      if (!s) continue
      const lower = s.toLowerCase()
      if (lower === 'nan' || lower === 'none' || lower === 'null') continue
      return s.replace(/^@+/, '')
    }
    return null
  }
  return pick(
    'Artist',
    'IG Full Name',
    'Instagram',
    'TikTok',
    'IG Username',
    'Primary Artist',
  )
}

/**
 * Preferred Analyze identity from a row: mapping id → platform UID → name.
 *
 * When ``requireArtistMapping`` is true (Snoop), skip rows with no artist_mappings
 * signal — IG User ID alone often has no follower/post history to Analyze.
 */
export function analyzeIdentityFromRow(row, { requireArtistMapping = false } = {}) {
  if (!row || typeof row !== 'object') return null
  if (requireArtistMapping && !rowHasArtistMapping(row)) return null
  const mappingId = cleanId(row.id)
  if (mappingId && /^\d+$/.test(mappingId)) {
    return { id: Number(mappingId), q: analyzeQueryFromRow(row) }
  }
  const igUid = cleanId(row['Instagram UID'] ?? row['IG User ID'])
  if (igUid) {
    return { ig_uid: igUid, q: analyzeQueryFromRow(row) }
  }
  const ttUid = cleanId(row['TikTok UID'])
  if (ttUid) {
    return { tt_uid: ttUid, q: analyzeQueryFromRow(row) }
  }
  const q = analyzeQueryFromRow(row)
  if (!q) return null
  return { q }
}

/** Column that should host the Analyze affordance (first match in table columns). */
export function analyzeLinkColumn(columns = []) {
  const set = new Set(columns)
  for (const c of ['Artist', 'IG Full Name', 'Instagram', 'IG Username', 'TikTok']) {
    if (set.has(c)) return c
  }
  return null
}

/** Build /analyze?... from an identity object or free-text query. */
export function analyzePath(identityOrQuery) {
  if (identityOrQuery == null || identityOrQuery === '') return '/analyze'
  if (typeof identityOrQuery === 'string') {
    const q = identityOrQuery.trim()
    if (!q) return '/analyze'
    return `/analyze?q=${encodeURIComponent(q)}`
  }
  const params = new URLSearchParams()
  if (identityOrQuery.id != null && identityOrQuery.id !== '') {
    params.set('id', String(identityOrQuery.id))
  }
  if (identityOrQuery.ig_uid) params.set('ig_uid', String(identityOrQuery.ig_uid))
  if (identityOrQuery.tt_uid) params.set('tt_uid', String(identityOrQuery.tt_uid))
  // Keep display name in URL when we only have UIDs / for shareability alongside id.
  if (identityOrQuery.q && !params.has('id')) {
    params.set('q', String(identityOrQuery.q))
  } else if (identityOrQuery.q && params.has('id')) {
    // optional label — skip to keep URL short when id present
  }
  const qs = params.toString()
  return qs ? `/analyze?${qs}` : '/analyze'
}

export function analyzePathFromRow(row, options = {}) {
  const identity = analyzeIdentityFromRow(row, options)
  return identity ? analyzePath(identity) : '/analyze'
}
